import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowBack } from '@mui/icons-material';

import Button from 'components/library/Button/Button';
import styles from './ConfirmationForm.module.css';
import ConfirmationPersonal from '../ConfirmationPersonal';
import ConfirmationSociety from '../ConfirmationSociety';
import stylesButton from '../ConfirmationSociety/ConfirmationSociety.module.css';
import { SocietyState } from 'store/features/society/types';

type ErrorForm = {
  societyAddress?: string;
  siren?: string;
  rcs?: string;
  nameSociety?: string;
  societyLegal?: string;
  role?: string;
}

interface Props {
  wantInvestBySociety: boolean,
  setIsComplete: (value: boolean) => void,
  step: number,
  setStep: (value: number) => void,
  children: React.ReactNode,
  submit: () => void,
  checkValidity: boolean,
}

export default function ConfirmationForm({
  wantInvestBySociety, setIsComplete, step, setStep, children,
  submit, checkValidity,
}: Props) {
  const [errors, setErrors] = useState<ErrorForm>({});
  const { t } = useTranslation();

  const validateSocietyData = (data: Partial<SocietyState>) => {
    const newErrors: ErrorForm = {};
    if (!data?.countrySociety || data?.countrySociety.trim() === '' || data?.countrySociety.length > 2) {
      newErrors.societyAddress = t('companyCoordonate.countryInvalid')
    }
    if (!data?.siren || data?.siren.trim() === '' || data?.siren.length > 30) {
      newErrors.siren = t('companyCoordonate.sirenInvalid')
    }
    if (!data?.rcs || data?.rcs.trim() === '' || data?.rcs.length > 128) {
      newErrors.rcs = t('companyCoordonate.rcsInvalid')
    }
    if (!data?.nameSociety || data?.nameSociety.trim() === '' || data?.nameSociety.length > 128) {
      newErrors.nameSociety = t('companyCoordonate.nameInvalid')
    }
    if (!data?.societyLegal || data?.societyLegal.trim() === '' || data?.societyLegal.length > 128) {
      newErrors.societyLegal = t('companyCoordonate.legalFormInvalid')
    }
    if (!data?.role || data?.role.trim() === '' || data?.role.length > 128) {
      newErrors.role = t('companyCoordonate.roleInvalid')
    }
    if (!data?.societyAddress || data?.societyAddress.trim() === '' || data?.societyAddress.length > 256) {
      newErrors.societyAddress = t('companyCoordonate.addressInvalid')
    }
    if (!data?.societyCity || data?.societyCity.trim() === '' || data?.societyCity.length > 64) {
      newErrors.societyAddress = t('companyCoordonate.cityInvalid')
    }
    if (!data?.societyZipCode || data?.societyZipCode.trim() === '' || data?.societyZipCode.length > 64) {
      newErrors.societyAddress = t('companyCoordonate.zipCodeInvalid')
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    } else {
      setErrors({});
      return true;
    }
  };

  const next = (values: Partial<SocietyState>) => {
    if (validateSocietyData(values)) {
      setStep(1);
    }
  };

  const previous = () => setStep(0);

  return (
    <div className={styles.confirmation}>
      <h1 className={styles.title}>
        {t('navigation.engagement')}
      </h1>
      <div className={styles.form}>
        {
          (step === 0 && wantInvestBySociety)
            ? (
              <>
                <ConfirmationSociety
                  errors={errors}
                >
                  {
                    (isComplete: boolean, values: Partial<SocietyState>) => (
                      <Button
                        label={t('button.next')}
                        className={stylesButton.next}
                        onButtonClick={() => next(values)}
                        disabled={!isComplete}
                        dataCy="confirmation-society-next"
                      />
                    )
                  }
                </ConfirmationSociety>
              </>
            )
            : (
              <>
                <ConfirmationPersonal
                  handleIsComplete={(bool: boolean) => setIsComplete(bool)}
                  submit={submit}
                  checkValidity={checkValidity}
                />
                {wantInvestBySociety
                  ? (
                    <div>
                      <Button
                        label={t('button.previous')}
                        icon={<ArrowBack />}
                        className={stylesButton.previous}
                        variant="ghost"
                        onButtonClick={previous}
                        dataCy="confirmation-personal-previous"
                      />
                      {children}
                    </div>
                  )
                  : (children)
                }
              </>
            )
        }
      </div>
    </div>
  );
}
